'use client';

import { c as _c } from "react/compiler-runtime";
import { type Effect, NOTIFICATIONS, PODCAST, TICKER } from './clickerData';

/**
 * Every visual layer Stimulation Clicker can turn on, contained in one
 * absolutely-positioned overlay.
 *
 * Two rules hold this together:
 *  - **`pointer-events-none` everywhere.** The whole joke is stuff piling on top
 *    of the game, and none of it may ever block the button underneath.
 *  - **Deterministic positions.** Offsets come from the element index, not
 *    `Math.random()`, so the server and client render identical markup. Random
 *    positions here would be a hydration mismatch on every load.
 */

type Props = {
  active: Set<Effect>;
  /** Copies owned, so stacked upgrades get visibly denser. */
  counts: Partial<Record<Effect, number>>;
};

/** Spread n items across the width using a cheap deterministic hash. */
const spread = (n: number) => Array.from({
  length: n
}, (_, i) => ({
  left: `${(i * 37 + 11) % 96}%`,
  delay: `${i * 13 % 40 / 10}s`,
  duration: `${2.4 + i * 7 % 20 / 10}s`
}));
const ClickerChaos = t0 => {
  const $ = _c(27);
  const {
    active,
    counts
  } = t0;
  let t1;
  if ($[0] !== active) {
    t1 = active.has("rain") && spread(28).map(_temp);
    $[0] = active;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== active || $[3] !== counts) {
    t2 = active.has("emoji") && spread(6 * (counts.emoji ?? 1)).map(_temp2);
    $[2] = active;
    $[3] = counts;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== active || $[6] !== counts) {
    t3 = active.has("notifications") && Array.from({
      length: counts.notifications ?? 1
    }, _temp3);
    $[5] = active;
    $[6] = counts;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== active) {
    t4 = active.has("podcast") && <div className="absolute left-3 top-3 max-w-[46%] rounded-xl border border-ds-border-standard bg-background-secondary/90 p-3"><p className="text-xs font-emphasis text-textPrimary">🎙️ THE GRIND CAST — live</p><p className="mt-1 text-xs italic text-textGray">“{PODCAST[0]}…”</p></div>;
    $[8] = active;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== active) {
    t5 = active.has("ticker") && <div className="absolute inset-x-0 top-0 overflow-hidden border-b border-ds-border-standard bg-destructive/85"><div className="animate-fun-marquee flex w-[200%] gap-8 whitespace-nowrap py-1 text-xs font-emphasis text-white">{[...TICKER, ...TICKER].map(_temp4)}</div></div>;
    $[10] = active;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== active) {
    t6 = active.has("subway") && <div className="absolute inset-x-0 bottom-0 h-20 overflow-hidden border-t border-ds-border-standard bg-background-light"><div className="animate-fun-marquee flex w-[200%] items-end gap-6 whitespace-nowrap py-2 text-2xl">{Array.from({
          length: 40
        }, _temp5)}</div><p className="absolute bottom-1 left-2 text-[10px] uppercase tracking-wide text-textGray">gameplay footage</p></div>;
    $[12] = active;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== active) {
    t7 = active.has("asmr") && <div className="absolute bottom-24 left-1/2 -translate-x-1/2 rounded-full border border-ds-border-standard bg-background-secondary/90 px-3 py-1"><p className="text-xs italic text-textGray">👂 …tk tk tk… so close…</p></div>;
    $[14] = active;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== active) {
    t8 = active.has("confetti") && spread(22).map(_temp6);
    $[16] = active;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t1 || $[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7 || $[25] !== t8) {
    t9 = <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">{t1}{t2}{t3}{t4}{t5}{t6}{t7}{t8}</div>;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
};
export default ClickerChaos;
function _temp(drop, i) {
  return <span key={`rain-${i}`} className="animate-fun-fall absolute top-0 text-sm opacity-50" style={{
    left: drop.left,
    animationDelay: drop.delay,
    animationDuration: drop.duration
  }}>|</span>;
}
function _temp2(item, i_0) {
  return <span key={`emoji-${i_0}`} className="animate-fun-drift absolute text-2xl opacity-70" style={{
    left: item.left,
    top: `${(i_0 * 23 + 8) % 80}%`,
    animationDelay: item.delay
  }}>{["\u2728", "\uD83D\uDCAB", "\uD83C\uDF1F", "\uD83D\uDD25", "\uD83D\uDC8E", "\uD83C\uDF88"][i_0 % 6]}</span>;
}
function _temp3(_, lane) {
  return NOTIFICATIONS.slice(lane * 2, lane * 2 + 2).map((text, i_1) => <span key={`notif-${lane}-${i_1}`} className="animate-fun-drift absolute rounded-lg border border-ds-border-standard bg-background-secondary px-2.5 py-1 text-xs text-textSecondary shadow-card" style={{
    right: `${4 + lane * 3}%`,
    top: `${12 + (lane * 2 + i_1) * 13}%`,
    animationDelay: `${(lane + i_1) * 0.6}s`
  }}>🔔 {text}</span>);
}
function _temp4(line, i_2) {
  return <span key={`tick-${i_2}`}>● {line}</span>;
}
function _temp5(__0, i_3) {
  return <span key={`sub-${i_3}`}>{["\uD83D\uDE87", "\uD83C\uDFC3", "\uD83E\uDE99", "\uD83D\uDEA7", "\uD83D\uDEF9", "\uD83C\uDFE2"][i_3 % 6]}</span>;
}
function _temp6(bit, i_4) {
  return <span key={`conf-${i_4}`} className="animate-fun-fall absolute top-0 text-base" style={{
    left: bit.left,
    animationDelay: bit.delay,
    animationDuration: `${1.6 + i_4 * 5 % 15 / 10}s`
  }}>{["\uD83C\uDF89", "\uD83C\uDF8A", "\uD83E\uDD73"][i_4 % 3]}</span>;
}